import React from 'react';
import { addDays, format, parseISO, setYear } from 'date-fns';
import { ClockIcon, PartyPopperIcon, PlaneIcon, UserPlusIcon, UsersIcon } from 'lucide-react';
import { BirthdaysCard } from '../components/dashboard/BirthdaysCard';
import { ChangeRequestsCard } from '../components/dashboard/ChangeRequestsCard';
import { KpiTile } from '../components/dashboard/KpiTile';
import { PendingRequestsCard } from '../components/dashboard/PendingRequestsCard';
import { RoleMetrics } from '../components/dashboard/RoleMetrics';
import { WhosOutCard } from '../components/dashboard/WhosOutCard';
import { Avatar } from '../components/ui/Avatar';
import { Badge } from '../components/ui/Badge';
import { Card } from '../components/ui/Card';
import { timeOffEntries } from '../data/timeOff';
import { useHRStore } from '../hooks/useHRStore';
import type { Navigate, PtoRequest } from '../types/hr';
import { displayName } from '../utils/employees';
import { TODAY, formatDate, relativeDay } from '../utils/format';
import { isOutOn } from '../utils/timeOff';
interface DashboardProps {
  requests: PtoRequest[];
  onDecide: (id: string, status: 'approved' | 'denied') => void;
  onNavigate: Navigate;
}
const TODAY_ISO = format(TODAY, 'yyyy-MM-dd');
const MONTH_PREFIX = format(TODAY, 'yyyy-MM');
export function Dashboard({
  requests,
  onDecide,
  onNavigate
}: DashboardProps) {
  const {
    employees
  } = useHRStore();
  const headcount = employees.length;
  const pending = requests.filter(r => r.status === 'pending');
  const oldestPending = pending.reduce<string | null>((oldest, r) => !oldest || r.submitted < oldest ? r.submitted : oldest, null);
  const outToday = timeOffEntries.filter(e => isOutOn(e, TODAY));
  const backTomorrow = outToday.filter(e => e.end === TODAY_ISO).length;
  const newHires = employees.filter(e => e.hireDate.startsWith(MONTH_PREFIX)).sort((a, b) => a.hireDate.localeCompare(b.hireDate));
  const started = newHires.filter(e => e.hireDate <= TODAY_ISO).length;
  const anniversaries = employees.filter(e => e.status !== 'Terminated').map(e => {
    const hire = parseISO(e.hireDate);
    return {
      employee: e,
      date: setYear(hire, TODAY.getFullYear()),
      years: TODAY.getFullYear() - hire.getFullYear()
    };
  }).filter(a => a.years >= 1 && a.date >= TODAY && a.date <= addDays(TODAY, 30)).sort((a, b) => a.date.getTime() - b.date.getTime());
  const departmentCount = new Set(employees.map(e => e.department)).size;
  return <div data-id="element-1175" data-mp="1jnre0ul18qy2">
      <div className="mb-8" data-id="element-1176" data-mp="1jo9g47udlzdw">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-azure" data-id="element-1177" data-mp="1jp2dwjbcxejw">{format(TODAY, 'EEEE, MMMM d, yyyy')}</p>
        <h1 className="mt-1 text-2xl font-semibold tracking-tight text-navy md:text-[28px]" data-id="element-1178" data-mp="1xko24o9rnub9">Good morning, Jordan</h1>
        <p className="mt-1.5 text-sm text-muted" data-id="element-1179" data-mp="391w5r7cl96ed">
          {pending.length > 0 ? `You have ${pending.length} time-off ${pending.length === 1 ? 'request' : 'requests'} waiting and ${outToday.length} people out today.` : `The request queue is clear. ${outToday.length} people are out today.`}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4" data-id="element-1180" data-mp="3hjy24a00cug6">
        <KpiTile label="Total headcount" value={headcount} note={`Across ${departmentCount} departments`} icon={UsersIcon} accent="navy" onClick={() => onNavigate({
        page: 'employees'
      })} data-id="element-1181" data-mp="32mp97bv23imc" />
        <KpiTile label="New hires this month" value={newHires.length} note={`${started} started · ${newHires.length - started} upcoming`} icon={UserPlusIcon} accent="azure" onClick={() => onNavigate({
        page: 'employees'
      })} data-id="element-1182" data-mp="3va1kuqzoi60v" />
        <KpiTile label="Out today" value={outToday.length} note={backTomorrow ? `${backTomorrow} back tomorrow` : 'See the team calendar'} icon={PlaneIcon} accent="deep" onClick={() => onNavigate({
        page: 'timeoff',
        tab: 'whos-out'
      })} data-id="element-1183" data-mp="3qjl34uotry8p" />
        <KpiTile label="Pending PTO requests" value={pending.length} note={oldestPending ? `Oldest submitted ${relativeDay(oldestPending).toLowerCase()}` : 'Nothing waiting'} icon={ClockIcon} accent="flame" onClick={() => onNavigate({
        page: 'timeoff',
        tab: 'requests'
      })} data-id="element-1184" data-mp="2vcdj5dzxx7a4" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3" data-id="element-1185" data-mp="2ggo45zt69uew">
        <div className="xl:col-span-2" data-id="element-1186" data-mp="3e1afroit8bvi">
          <PendingRequestsCard requests={requests} onDecide={onDecide} onNavigate={onNavigate} data-id="element-1187" data-mp="3e46br7b3kqhb" />
        </div>
        <WhosOutCard onNavigate={onNavigate} data-id="element-1188" data-mp="34ciqxp91q7ot" />
      </div>

      <div className="mt-8" data-id="element-1189" data-mp="34axufu368eob">
        <RoleMetrics onNavigate={onNavigate} data-id="element-1190" data-mp="34a5y2h1r2cwz" />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-2 xl:grid-cols-3" data-id="element-1191" data-mp="2zcny3g5pn814">
        <Card title="Upcoming anniversaries" subtitle={`${anniversaries.length} in the next 30 days`} icon={PartyPopperIcon} bodyClassName="px-3 pb-3" data-id="element-1192" data-mp="np3g4kpqu4z">
          <ul className="max-h-[360px] space-y-0.5 overflow-y-auto" data-id="element-1193" data-mp="3s0a1anb10ang">
            {anniversaries.map(({
            employee,
            date,
            years
          }) => {
            const isToday = date.getTime() === TODAY.getTime();
            return <li key={employee.id} data-id="element-1194" data-mp="d6b3ktn7ok1w">
                  <button type="button" onClick={() => onNavigate({
                page: 'profile',
                employeeId: employee.id
              })} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-canvas/70" data-id="element-1195" data-mp="d4ged34wq5s0">
                    <Avatar name={displayName(employee)} seed={employee.id} size="sm" data-id="element-1196" data-mp="r49d9lql7sb2" />
                    <span className="min-w-0 flex-1" data-id="element-1197" data-mp="vxu2ope47dyn">
                      <span className="block truncate text-sm font-medium text-navy" data-id="element-1198" data-mp="10vm3k8wqmaez">{displayName(employee)}</span>
                      <span className="block truncate text-xs text-muted" data-id="element-1199" data-mp="15v8icms7rypw">{employee.department}</span>
                    </span>
                    <span className="text-right" data-id="element-1200" data-mp="1aul577j355sh">
                      <span className="block text-sm font-semibold text-navy" data-id="element-1201" data-mp="l5nswvvm5lmu">
                        {years} {years === 1 ? 'year' : 'years'}
                      </span>
                      {isToday ? <Badge tone="orange" data-id="element-1202" data-mp="g4gnh3gy0yjc">Today</Badge> : <span className="text-xs text-muted" data-id="element-1203" data-mp="uvza3k4b5rbo">{format(date, 'MMM d')}</span>}
                    </span>
                  </button>
                </li>;
          })}
          </ul>
        </Card>

        <Card title="New hires" subtitle={`${newHires.length} this month`} icon={UserPlusIcon} bodyClassName="px-3 pb-3" data-id="element-1204" data-mp="13v490rjs7mod">
          <ul className="space-y-0.5" data-id="element-1205" data-mp="ywswua60l02z">
            {newHires.map(e => {
            const hasStarted = e.hireDate <= TODAY_ISO;
            return <li key={e.id} data-id="element-1206" data-mp="18uyx6nc69y5q">
                  <button type="button" onClick={() => onNavigate({
                page: 'profile',
                employeeId: e.id
              })} className="flex w-full items-center gap-3 rounded-xl px-3 py-2.5 text-left transition hover:bg-canvas/70" data-id="element-1207" data-mp="18vrukxp8rbb7">
                    <Avatar name={displayName(e)} seed={e.id} size="sm" data-id="element-1208" data-mp="1q5vva9puozsk" />
                    <span className="min-w-0 flex-1" data-id="element-1209" data-mp="1v3xcep3pup1e">
                      <span className="block truncate text-sm font-medium text-navy" data-id="element-1210" data-mp="1v5grwdhjsw24">{displayName(e)}</span>
                      <span className="block truncate text-xs text-muted" data-id="element-1211" data-mp="1gcx0ga3rgd0i">
                        {e.jobTitle} · {e.department}
                      </span>
                    </span>
                    <Badge tone={hasStarted ? 'green' : 'blue'} dot data-id="element-1212" data-mp="17g60vnq02g16">
                      {hasStarted ? 'Started' : 'Starts'} {formatDate(e.hireDate, 'MMM d')}
                    </Badge>
                  </button>
                </li>;
          })}
          </ul>
        </Card>

        <div className="lg:col-span-2 xl:col-span-1" data-id="element-1213" data-mp="12f8fl29p15vg">
          <BirthdaysCard onNavigate={onNavigate} data-id="element-1214" data-mp="2rtn4pnomiurh" />
        </div>
      </div>

      <div className="mt-6" data-id="element-1215" data-mp="2wsrbk7o2sgen">
        <ChangeRequestsCard onNavigate={onNavigate} data-id="element-1216" data-mp="2wryc7rrg4t6j" />
      </div>
    </div>;
}